import React, { useState } from 'react';
import { Star, ThumbsUp, Clock, Award, MessageSquare, User, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';

interface Rating {
  id: string;
  cliente_id: number;
  cliente_nome: string;
  fornecedor_id: number;
  fornecedor_nome: string;
  pedido_id: number;
  pontuacao: number;
  comentario: string;
  categorias: {
    qualidade: number;
    prazo: number;
    comunicacao: number;
    preco: number;
  };
  created_at: string;
}

interface RatingFormProps {
  fornecedor_id: number;
  fornecedor_nome: string;
  pedido_id: number;
  onSubmit: (rating: Omit<Rating, 'id' | 'cliente_id' | 'cliente_nome' | 'created_at'>) => Promise<void>;
  onCancel: () => void;
}

interface RatingDisplayProps {
  ratings: Rating[];
  averageRating: number;
  totalRatings: number;
  showDetails?: boolean;
}

const RatingForm: React.FC<RatingFormProps> = ({
  fornecedor_id,
  fornecedor_nome,
  pedido_id,
  onSubmit,
  onCancel
}) => {
  const [pontuacao, setPontuacao] = useState(5);
  const [comentario, setComentario] = useState('');
  const [categorias, setCategorias] = useState({
    qualidade: 5,
    prazo: 5,
    comunicacao: 5,
    preco: 5
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comentario.trim()) {
      toast.error('Por favor, adicione um comentário');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        fornecedor_id,
        fornecedor_nome,
        pedido_id,
        pontuacao,
        comentario: comentario.trim(),
        categorias
      });
      toast.success('Avaliação enviada com sucesso!');
      onCancel();
    } catch (error) {
      toast.error('Erro ao enviar avaliação');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (value: number, onChange: (value: number) => void, size: 'sm' | 'md' = 'md') => {
    const stars = [];
    const starSize = size === 'sm' ? 'h-4 w-4' : 'h-6 w-6';
    
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          className={`transition-colors ${
            i <= value ? 'text-yellow-400' : 'text-gray-300'
          } hover:text-yellow-400`}
        >
          <Star className={`${starSize} fill-current`} />
        </button>
      );
    }
    
    return <div className="flex space-x-1">{stars}</div>;
  };

  return (
    <div className="card p-6">
      <h3 className="text-xl font-bold text-gray-dark mb-4">
        Avaliar {fornecedor_nome}
      </h3>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Avaliação Geral */}
        <div>
          <label className="block text-sm font-medium text-gray-dark mb-2">
            Avaliação Geral
          </label>
          <div className="flex items-center space-x-4">
            {renderStars(pontuacao, setPontuacao)}
            <span className="text-sm text-gray-medium">
              {pontuacao}/5 estrelas
            </span>
          </div>
        </div>

        {/* Categorias Específicas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-dark mb-2">
              Qualidade do Produto
            </label>
                         <div className="flex items-center space-x-2">
               {renderStars(categorias.qualidade, (value) => setCategorias(prev => ({ ...prev, qualidade: value })), 'sm')}
               <span className="text-xs text-gray-medium">{categorias.qualidade}/5</span>
             </div>
           </div>

           <div>
             <label className="block text-sm font-medium text-gray-dark mb-2">
               Prazo de Entrega
             </label>
             <div className="flex items-center space-x-2">
               {renderStars(categorias.prazo, (value) => setCategorias(prev => ({ ...prev, prazo: value })), 'sm')}
               <span className="text-xs text-gray-medium">{categorias.prazo}/5</span>
             </div>
           </div>

           <div>
             <label className="block text-sm font-medium text-gray-dark mb-2">
               Comunicação
             </label>
             <div className="flex items-center space-x-2">
               {renderStars(categorias.comunicacao, (value) => setCategorias(prev => ({ ...prev, comunicacao: value })), 'sm')}
               <span className="text-xs text-gray-medium">{categorias.comunicacao}/5</span>
             </div>
           </div>

           <div>
             <label className="block text-sm font-medium text-gray-dark mb-2">
               Relação Preço/Qualidade
             </label>
             <div className="flex items-center space-x-2">
               {renderStars(categorias.preco, (value) => setCategorias(prev => ({ ...prev, preco: value })), 'sm')}
               <span className="text-xs text-gray-medium">{categorias.preco}/5</span>
             </div>
          </div>
        </div>

        {/* Comentário */}
        <div>
          <label className="block text-sm font-medium text-gray-dark mb-2">
            Comentário (Obrigatório)
          </label>
          <textarea
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            placeholder="Conte sua experiência com este fornecedor..."
            className="form-input h-24 resize-none"
            required
          />
        </div>

        {/* Botões */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-outline"
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting || !comentario.trim()}
          >
            {isSubmitting ? 'Enviando...' : 'Enviar Avaliação'}
          </button>
        </div>
      </form>
    </div>
  );
};

const RatingDisplay: React.FC<RatingDisplayProps> = ({
  ratings,
  averageRating,
  totalRatings,
  showDetails = false
}) => {
  const getRatingColor = (rating: number) => {
    if (rating >= 4.5) return 'text-green-600';
    if (rating >= 4.0) return 'text-blue-600';
    if (rating >= 3.0) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getBadge = (rating: number) => {
    if (rating >= 4.8) return { text: 'Excelente', color: 'bg-green-100 text-green-800' };
    if (rating >= 4.5) return { text: 'Muito Bom', color: 'bg-blue-100 text-blue-800' };
    if (rating >= 4.0) return { text: 'Bom', color: 'bg-yellow-100 text-yellow-800' };
    if (rating >= 3.0) return { text: 'Regular', color: 'bg-orange-100 text-orange-800' };
    return { text: 'Precisa Melhorar', color: 'bg-red-100 text-red-800' };
  };

  const badge = getBadge(averageRating);

  const renderStars = (rating: number, size: 'sm' | 'md' = 'md') => {
    const stars = [];
    const starSize = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';
    
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          className={`${starSize} ${
            i <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
          }`}
        />
      );
    }
    
    return <div className="flex space-x-1">{stars}</div>;
  };

  return (
    <div className="space-y-4">
      {/* Resumo da Avaliação */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-dark">Avaliações</h3>
          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${badge.color}`}>
            {badge.text}
          </span>
        </div>

        <div className="flex items-center space-x-6">
          <div className="text-center">
            <div className={`text-3xl font-bold ${getRatingColor(averageRating)}`}>
              {averageRating.toFixed(1)}
            </div>
            <div className="flex justify-center mt-1">
              {renderStars(averageRating)}
            </div>
            <div className="text-sm text-gray-medium mt-1">
              {totalRatings} avaliações
            </div>
          </div>

          <div className="flex-1">
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map(stars => {
                const count = ratings.filter(r => Math.round(r.pontuacao) === stars).length;
                const percentage = totalRatings > 0 ? (count / totalRatings) * 100 : 0;
                
                return (
                  <div key={stars} className="flex items-center space-x-2">
                    <span className="text-sm text-gray-medium w-8">{stars}★</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-yellow-400 h-2 rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-medium w-12">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Avaliações */}
      {showDetails && ratings.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-dark">Avaliações Recentes</h4>
          {ratings.slice(0, 5).map((rating) => (
            <div key={rating.id} className="card p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-medium" />
                  <span className="font-medium text-gray-dark">{rating.cliente_nome}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-gray-medium" />
                  <span className="text-sm text-gray-medium">
                    {new Date(rating.created_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 mb-2">
                {renderStars(rating.pontuacao, 'sm')}
                <span className="text-sm text-gray-medium">{rating.pontuacao}/5</span>
              </div>
              
              <p className="text-gray-dark text-sm">{rating.comentario}</p>
              
              {/* Categorias */}
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="flex items-center space-x-1">
                  <Award className="h-3 w-3 text-blue-500" />
                  <span className="text-xs text-gray-medium">Qualidade: {rating.categorias.qualidade}/5</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="h-3 w-3 text-green-500" />
                  <span className="text-xs text-gray-medium">Prazo: {rating.categorias.prazo}/5</span>
                </div>
                <div className="flex items-center space-x-1">
                  <MessageSquare className="h-3 w-3 text-purple-500" />
                  <span className="text-xs text-gray-medium">Comunicação: {rating.categorias.comunicacao}/5</span>
                </div>
                <div className="flex items-center space-x-1">
                  <ThumbsUp className="h-3 w-3 text-orange-500" />
                  <span className="text-xs text-gray-medium">Preço: {rating.categorias.preco}/5</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export { RatingForm, RatingDisplay };
export type { Rating }; 